/**
 * @architecture-header 1
 * @layer shared
 * @domain visual-language
 * @responsibility Render the list-column primitives: rows, groups, empty states and filter rows.
 * @state-owner navigation store owns group collapse state
 * @allowed-imports React; shared icons; workspace store
 * @forbidden business rules; network
 * @public-api workspace shell and modules
 * @side-effects none
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import type { MouseEvent, ReactNode } from 'react';
import { useWorkspaceUiStore } from '../model/workspace-store';
import { ListChevronIcon } from './VispoIcon';

export interface ListRowProps {
  readonly icon: ReactNode;
  readonly title: string;
  readonly subtitle: string;
  readonly on?: boolean;
  readonly read?: boolean;
  readonly dim?: boolean;
  readonly background?: string;
  readonly foreground?: string;
  readonly flag?: string;
  readonly flagWarn?: boolean;
  readonly time?: string;
  readonly onSelect?: () => void;
  readonly onContextMenu?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export function ListRow({
  icon,
  title,
  subtitle,
  on = false,
  read = false,
  dim = false,
  background,
  foreground,
  flag,
  flagWarn = false,
  time,
  onSelect,
  onContextMenu,
}: ListRowProps) {
  const hot = (background ?? '').includes('error');
  const chipBackground = hot ? background : 'var(--sc)';
  const chipForeground = hot ? foreground : 'var(--outline)';
  return (
    <button
      className={`r3 ${on ? 'on' : ''} ${read ? 'read' : ''} ${dim ? 'dim' : ''}`}
      type="button"
      onClick={onSelect}
      onContextMenu={onContextMenu}
    >
      <span className="sq3" style={{ background: chipBackground, color: chipForeground }}>
        {icon}
      </span>
      <span className="c">
        <span className="l1">
          <b>{title}</b>
        </span>
        <span className="l2">
          <i>{subtitle}</i>
          {flag ? (
            <span className={`fl2 ${flagWarn ? 'w' : ''}`}>{flag}</span>
          ) : (
            <em>{time ?? ''}</em>
          )}
        </span>
      </span>
    </button>
  );
}

export function ListGroup({
  id,
  label,
  count,
  children,
}: {
  id: string;
  label: ReactNode;
  count?: number | null;
  children: ReactNode;
}) {
  const collapsed = useWorkspaceUiStore((state) => Boolean(state.collapsedGroups[id]));
  const toggleGroup = useWorkspaceUiStore((state) => state.toggleGroup);
  return (
    <>
      <button className="vlg" type="button" onClick={() => toggleGroup(id)}>
        <ListChevronIcon collapsed={collapsed} />
        <span>{label}</span>
        {count != null ? <em>{count}</em> : null}
      </button>
      {collapsed ? null : children}
    </>
  );
}

function EmptyTrayIcon() {
  return (
    <svg aria-hidden="true" className="empty-state__icon" viewBox="0 0 64 64">
      <path d="M12 23h40l-3.8 26H15.8L12 23Z" />
      <path d="M18 23l5-10h18l5 10M12 35h12l3 5h10l3-5h12" />
    </svg>
  );
}

export function ListEmpty({ description, title }: { description: ReactNode; title: ReactNode }) {
  return (
    <div className="empty-state list-empty">
      <EmptyTrayIcon />
      <b>{title}</b>
      <span>{description}</span>
    </div>
  );
}

/** The left-column filter row. */
export function ListFilterRow({
  on,
  label,
  count,
  onSelect,
  onContextMenu,
}: {
  on: boolean;
  label: string;
  count?: ReactNode;
  onSelect: () => void;
  onContextMenu?: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      className={`vfr ${on ? 'on' : ''}`}
      type="button"
      onClick={onSelect}
      onContextMenu={onContextMenu}
    >
      <span className="vfd" />
      <span className="vft">{label}</span>
      {count != null ? <span className="vfn">{count}</span> : null}
    </button>
  );
}

export function MainEmpty({ title, description }: { title: ReactNode; description: ReactNode }) {
  return (
    <div className="empty-state mempty">
      <EmptyTrayIcon />
      <b>{title}</b>
      <span>{description}</span>
    </div>
  );
}

/** The class grouping header used by `byClass()`. */
export function ListClassHeading({ label, count }: { label: string; count: number }) {
  return (
    <div className="lgrp">
      {label}
      <span className="c2">{count}</span>
    </div>
  );
}
